import React, { useState, useEffect } from 'react';
import cs from 'classnames';
import { recursionEach, dateFns, deepCopy, isArray } from '@lhb/func';
import { getCookie, getStorage, setCookie, setStorage } from '@lhb/cache';
import { tenantCheck, getConfigCustomField } from '@/common/api/common';
import { currentUserInfo, CurrentUserInfo, UserInfoModuleList } from '@/common/api/brief';
import { dispatchNavigate } from '@/common/document-event/dispatch';
import { getTenantInfo } from '@/common/api/system';
import { changeTenantInfo, UPDATE_TENANT_CHECK_INFO, UPDATE_CURRENT_USER_INFO } from '@/store/common';
import { UserInfoContextProvider } from './context';
import { devMenu } from './menuConfig';
import { defaultMapStyle, filterTargetLevelIcon } from './ts-config';
import { useDispatch } from 'react-redux';
import './theme.global.less';
import styles from './index.module.less';
import { Layout, Typography } from 'antd';
import IconFont from '@/common/components/IconFont';

export const siderWrap = 'siderWrap';

const { Content } = Layout;

export interface UserInfoProps extends CurrentUserInfo {
  haveMenu?: boolean;
  menusItems: any[];
  getTenantDetail?: Function;
  tenantInfo?: any;
  pageConfig?:any;
  menuList?: any[];
  mapConfig?: any;
}

/** 无菜单的基础布局
 * @description 逻辑同 src/layout/index.tsx，区别是该页面没有左侧和顶部导航，可用于短链分享页；
 * 而 src/layout/index.tsx 会判断页面路由是不是在导航内，如果不在就回到首页
 */
const BasicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const [userInfo, setUserInfo] = useState<UserInfoProps>({
    haveMenu: true,
    menusItems: [],
  });
  const [tenantInfo, setTenantInfo] = useState<any>({});
  const [pageConfig, setPageConfig] = useState<any>({});// 页面配置
  setPageConfig;
  const isDev = process.env.NODE_ENV === 'development'; // 是否是开发环境
  const [mapConfig, setMapConfig] = useState<any>({
    bg: getStorage('MAP_BG_CONFIG') || defaultMapStyle // 默认标准
  });
  setMapConfig;

  useEffect(() => {
    getCurrentUser();
    getTenantDetail();
    getTargetTenent();
  }, []);

  useEffect(() => {
    const tenantId = getCookie('tenantId');// 租户id
    const employeeId = getCookie('employeeId');// 员工id
    const msg: any = {
      report_time: dateFns.currentTime('', false),
    };
    if (tenantId || tenantInfo?.id) msg.tenant_id = tenantId || tenantInfo?.id;
    if (employeeId || userInfo?.id) msg.user_id = employeeId || userInfo?.id;

    // 部分子页面访问时上报归属的父级页面 https://confluence.lanhanba.com/pages/viewpage.action?pageId=67530354
    const { url } = pageConfig;
    url && window.LHBbigdata.send({
      msg: {
        url,
        ...msg
      }
    });
  }, [pageConfig, tenantInfo?.id, userInfo?.id]);

  // useEffect(() => {
  //   const isShareHasToken = getStorage('IS_SHARE__HAS_TOEKN');
  //   if (isShareHasToken) {
  //     removeCookie('flow_token');
  //     removeCookie('flow_share_token');
  //     removeStorage('IS_SHARE__HAS_TOEKN');
  //     window.location.replace('/');
  //     return;
  //   }
  // }, [userInfo]);

  // 将菜单结果处理为menu需要的格式
  const dealWithMenu = (list: any[], hiddenEncodes: string[] = [], isRoot = true) => {
    if (Array.isArray(list) && list.length) {

      // 过滤特殊需求隐藏菜单
      const tmpList = (isArray(hiddenEncodes) && hiddenEncodes.length) ? list.filter((itm: any) => !hiddenEncodes.includes(itm.encode)) : list;

      return tmpList.map((item) => {
        // const simpleUri = item.simpleUri;
        // const uri = item.uri;
        // // 如果存在simpleUri,就把simpleUri和uri做替换
        // if (simpleUri) {
        //   item.simpleUri = uri;
        //   item.uri = simpleUri;
        // }
        const newChildren = dealWithMenu(item.children, hiddenEncodes, false);
        const ToolText = (
          <Typography.Text
            className='customMenuItem'
            ellipsis={{
              tooltip: {
                placement: 'right',
                // 如果是收起的时候，就不再使用label的tooltip，因为menu有默认的tooltip
                title: item.name,
                overlayClassName: styles.collapsedTooltip,
              }
            }}
          >
            {item.name}
          </Typography.Text>
        );
        const newItem = {
          label: (
            isRoot ? (
              <div
                onMouseEnter={() => {
                  // 确保在收起模式下，tooltip失效，因为menu在收起时，默认就有tooltip。会导致两个tooltip重叠
                  // if (methods.getCollapsed()) {
                  //   e.stopPropagation();
                  //   e.preventDefault();
                  // }
                }}
              >
                {ToolText}
              </div>
            ) : ToolText
          ),
          key: item.uri || item.encode,
          icon: item.icon && <IconFont className={styles.menuIcon} iconHref={item.icon} />,
          children: newChildren,
          // title: false // 设置false可以关闭收起状态下的tooltip
        };
        if (newChildren?.length) {
          newItem.popupClassName = styles.sliderItemPopup;
        }
        return newItem;
      });
    } else {
      return null;
    }
  };

  // 将菜单处理为平铺的数组
  const getMenuMap = (list: any[]) => {
    if (Array.isArray(list) && list.length) {
      let allMenus: any[] = [];
      list.forEach((item: any) => {
        const itm = deepCopy(item);
        delete itm.children;
        allMenus.push(itm);

        const children: any[] = getMenuMap(item.children);
        if (isArray(children) && children.length) {
          allMenus = allMenus.concat(children);
        }
      });
      return allMenus;
    } else {
      return [];
    }
  };

  // 获取用户信息（包含菜单列表）
  const getCurrentUser = async () => {
    try {
      const userInfo: CurrentUserInfo = await currentUserInfo();
      dispatch(UPDATE_CURRENT_USER_INFO(userInfo));
      let { moduleList } = userInfo;
      const employeeId = getCookie('employeeId');
      !employeeId && setCookie('employeeId', userInfo.id);
      userInfo?.technicalMobile && setStorage('technicalMobile', userInfo.technicalMobile);
      if (Array.isArray(moduleList) && moduleList.length) {
        filterTargetLevelIcon(moduleList);
        moduleList = moduleList.filter((moduleItem: any) => moduleItem.uri);
      }
      if (isDev && moduleList) {
        // 本地开发时可自定义配置
        moduleList = moduleList.concat(devMenu); // devMenu;
      }
      // 将simpleUri和uri对调，为了解决uri路由映射到simpleUri别名
      recursionEach(moduleList, 'children', (item) => {
        const simpleUri = item.simpleUri;
        const uri = item.uri;
        // 如果存在simpleUri,就把simpleUri和uri做替换
        if (simpleUri) {
          item.simpleUri = uri;
          item.uri = simpleUri;
        }
      });
      const menusItems: any[] = dealWithMenu(moduleList || []);
      const menuList: any[] = getMenuMap(moduleList || []);
      // setMenus(menuList);

      setUserInfo({
        ...userInfo,
        haveMenu: !!(moduleList as UserInfoModuleList[]).length,
        moduleList,
        menuList,
        menusItems,
        getTenantDetail
      });
    } catch (error) {
      console.log('error', error);
      dispatchNavigate('/404');
    }
  };

  const setLogo = (uri) => {
    const link: any = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = uri;
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  const getTenantDetail = async () => { // 获取当前租户详情，并存入适配的主题模型
    try {
      const data = await getTenantInfo();
      const tenantId = getCookie('tenantId');
      !tenantId && setCookie('tenantId', data.id);
      dispatch(changeTenantInfo({ ...data }));
      setTenantInfo({ ...data });
      data.webHeaderUri && setLogo(data.webHeaderUri);
      // 获取用户自定义存储的主题模型
      const { fields } = await getConfigCustomField({
        module: 'globalUserCustomThemeForLocation'
      });
      let newTheme = data.subjectColor;
      if (fields) {
        const res = JSON.parse(fields);
        if (typeof res === 'string') {
          newTheme = res;
        } else {
          newTheme = res.theme;
        }
      }
      setCookie('location-theme', newTheme || 'default');
    } catch (error) {}
  };

  const getTargetTenent = () => {
    tenantCheck().then((data: any) => {
      // setIsXP(data.isXiaoPeng);
      dispatch(UPDATE_TENANT_CHECK_INFO(data));
    });
  };

  return (
    <Layout className={styles.layout}>
      <UserInfoContextProvider
        userInfo={userInfo}
        tenantInfo={tenantInfo}
        getTenantDetail={getTenantDetail}
        pageConfig={pageConfig}
        mapConfig={mapConfig}
      >
        <Content className={cs(styles.content, styles.isFull)}>
          {children}
        </Content>
      </UserInfoContextProvider>
    </Layout>
  );
};

export default BasicLayout;
